/**
 * OnboardingCoach — Sticky-note variant (2026-05-26, Matt's pick).
 * DemoWatchNote — "This is a demo" indicator (2026-05-27, Matt's option #3).
 *
 * Visual language: warm-yellow paper (#FFF6D6), tape strip at the top,
 * dashed tan border + spine, Caveat handwriting font for the headline and
 * the active-step "← now" marker, slight rotation, soft drop shadow.
 * Reads as "hand-placed note from a teammate", clearly NOT part of the
 * product chrome (which uses cream/stone with solid borders + sans-serif).
 *
 * Desktop (≥641px): 268px wide sticky note pinned to the far right of the
 * viewport via EmptyStateDemo's absolute-positioned wrapper. Shows the full
 * 5-step list with active/complete state. Tilt: -1.4°.
 *
 * Mobile (≤640px): smaller sticky note rendered between the CTA and the
 * input bar, with a downward caret pointing at the input. Shows only the
 * current step (no list). Tilt: -1.5°. Description trimmed to one line.
 *
 * Step state is derived from the EmptyStateDemo Phase prop:
 *   pre-start → 1
 *   typing    → 2
 *   parsing   → 3
 *   lanes     → 3
 *   streaming → 4
 *   celebrate → 5
 *   settled   → 5
 *
 * The benefits sub-list (5a / 5b / 5c) reveals when step 5 is active —
 * desktop expands them inline under step 5; mobile cycles through them
 * every 1.2s in place of the description.
 *
 * Feature-flag gated via VITE_ONBOARDING_COACH_ENABLED. The persistent
 * dismiss flag is `localStorage["pv2-onboarding-coach-dismissed"]`. When that
 * flag is "true", the coach renders nothing (and PrimateVisionV2.tsx also
 * removes the demo + coach surface via its showCarousel guard).
 *
 * Caveat font is loaded in index.html alongside Inter and JetBrains Mono.
 */

import { type CSSProperties, type ReactElement, useEffect, useState } from 'react'
import { L, MODEL_DISPLAY_NAME } from './pv2-shared'

// EmptyStateDemo's Phase type is local; we mirror only the values we react to.
export type OnboardingCoachPhase =
  | 'pre-start'
  | 'typing'
  | 'parsing'
  | 'lanes'
  | 'streaming'
  | 'celebrate'
  | 'settled'
  | 'dismissed'

export interface OnboardingCoachProps {
  phase: OnboardingCoachPhase
  isMobile: boolean
  showDismissControls: boolean
  onSessionDismiss: () => void
  onPersistentDismiss: () => void
  /**
   * 2026-05-28 (Matt #engineering-jeff): the `streaming` phase is overloaded
   * — it covers both the post-submit "step 4 button highlight" window
   * (~3.8s) AND the longer "step 5 results" window where the narrative log
   * is the focal point. Without this hint, the sticky note would stay on
   * step 4 "Submit to Darwin" for the entire streaming phase, while the
   * cyan glow has already moved to the narrative log — the exact mismatch
   * Matt screenshotted: "still shows the highlight of the wrong item".
   *
   * Set to true once the highlight overlay has transitioned from the submit
   * button to the narrative log so the sticky note can bump to step 5.
   * Defaults to false. EmptyStateDemo owns the timer that drives this.
   */
  streamingPastSubmitHighlight?: boolean
}

const DISMISS_KEY = 'pv2-onboarding-coach-dismissed'

// ─── Sticky-note palette ─────────────────────────────────────────────
// Kept local to this component so the global L tokens stay product-only.
const STICKY_BG     = '#FFF6D6'
const STICKY_BORDER = '#B79A40'
const STICKY_INK    = '#1A1916'
const STICKY_MUTED  = '#8A7430'
const STICKY_DESC   = '#5C4D26'
const STICKY_TAPE   = 'rgba(220, 200, 130, 0.55)'

const CAVEAT_STACK = '"Caveat", "Bradley Hand", "Comic Sans MS", cursive'

interface StepDef {
  index: number
  title: string
  description: string  // desktop (full) — mobile uses descriptionShort
  descriptionShort: string  // mobile — single line, trimmed
}

const STEPS: StepDef[] = [
  {
    index: 1,
    title: 'Select your video source',
    description: 'Webcam or file upload',
    descriptionShort: 'Webcam or file.',
  },
  {
    index: 2,
    title: 'Enter your prompt',
    description: 'Closed, objectively verifiable questions work best',
    descriptionShort: 'Closed, verifiable questions work best.',
  },
  {
    index: 3,
    title: 'Prompt is compiled to JSON',
    description: 'Subject, action, conditions — structured intent',
    descriptionShort: 'Structured intent Darwin can verify.',
  },
  {
    index: 4,
    title: 'Submit to Darwin',
    description: `${MODEL_DISPLAY_NAME} (JEPA predictive world model)`,
    descriptionShort: `${MODEL_DISPLAY_NAME}, on-device.`,
  },
  {
    index: 5,
    title: 'Results',
    description: 'JSON, converted to readable narrative',
    descriptionShort: 'JSON → readable narrative.',
  },
]

interface BenefitDef { id: '5a' | '5b' | '5c'; text: string; textShort: string }
const BENEFITS: BenefitDef[] = [
  {
    id: '5a',
    text: 'Hallucination is impossible — grounded in pixels, not language priors',
    textShort: 'Hallucination is impossible.',
  },
  {
    id: '5b',
    text: '30fps processing — real-time inference',
    textShort: '30fps — real-time inference.',
  },
  {
    id: '5c',
    text: 'Action recognition — not just object detection',
    textShort: 'Action, not just objects.',
  },
]

function phaseToStepIndex(
  phase: OnboardingCoachPhase,
  streamingPastSubmitHighlight = false,
): number {
  switch (phase) {
    case 'pre-start': return 1
    case 'typing':    return 2
    case 'parsing':   return 3
    case 'lanes':     return 3
    case 'streaming': return streamingPastSubmitHighlight ? 5 : 4
    case 'celebrate': return 5
    case 'settled':   return 5
    case 'dismissed': return 1
  }
}

function isFlagEnabled(): boolean {
  // Vite production / dev: import.meta.env.VITE_ONBOARDING_COACH_ENABLED is
  // statically replaced at build time. In vitest, `vi.stubEnv` populates
  // process.env but not import.meta.env, so we also check process.env to keep
  // the unit-test surface honest.
  try {
    const metaEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    if (metaEnv?.VITE_ONBOARDING_COACH_ENABLED === 'true') return true
  } catch { /* ignore */ }
  try {
    if (typeof process !== 'undefined' && process.env?.VITE_ONBOARDING_COACH_ENABLED === 'true') return true
  } catch { /* ignore */ }
  return false
}

function isPersistentlyDismissed(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(DISMISS_KEY) === 'true'
  } catch {
    return false
  }
}

export function OnboardingCoach(props: OnboardingCoachProps): ReactElement | null {
  if (!isFlagEnabled()) return null
  if (isPersistentlyDismissed()) return null

  return props.isMobile ? <MobileStickyNote {...props} /> : <DesktopStickyNote {...props} />
}

// Exposed for tests and for EmptyStateDemo to keep the highlight overlay's
// sub-phase clock in sync with the coach step indicator.
export { phaseToStepIndex }

// ─────────────────────────────────────────────────────────────────────
// Desktop sticky note
// ─────────────────────────────────────────────────────────────────────

function DesktopStickyNote({
  phase, showDismissControls, onSessionDismiss, onPersistentDismiss,
  streamingPastSubmitHighlight,
}: OnboardingCoachProps): ReactElement {
  const activeIdx = phaseToStepIndex(phase, streamingPastSubmitHighlight)

  // Fixed sticky-note dimensions (2026-05-26 per Matt: "make the 'post it'
  // a fixed size that fits anything so it isn't resizing"). Height is sized
  // to fit the WORST case (step 5 active with the 3 benefit bullets expanded
  // inline). All earlier steps render in the same shell with extra blank
  // paper below — deliberately, so the note doesn't reflow as steps advance.
  // Measured 2026-05-26 with 5 steps + step-5 benefits = ~440px content;
  // 460 gives ~20px of breathing room.
  const NOTE_FIXED_HEIGHT = 460

  const noteStyle: CSSProperties = {
    width: 240,
    height: NOTE_FIXED_HEIGHT,
    flexShrink: 0,
    background: STICKY_BG,
    border: 'none',
    borderRadius: 4,
    padding: '18px 18px 14px',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: STICKY_INK,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    alignSelf: 'flex-start',
    transform: 'rotate(-1.4deg)',
    transformOrigin: 'top right',
    boxShadow:
      '0 1px 0 rgba(0,0,0,0.04), ' +
      '0 8px 24px -10px rgba(70, 50, 0, 0.18), ' +
      '0 30px 40px -20px rgba(70, 50, 0, 0.10)',
    position: 'relative',
  }

  const headerTitle: CSSProperties = {
    fontFamily: CAVEAT_STACK,
    fontSize: 26,
    fontWeight: 600,
    margin: 0,
    lineHeight: 1.05,
    letterSpacing: '0.005em',
    color: STICKY_INK,
  }

  return (
    <aside
      data-testid="onboarding-coach"
      data-variant="sticky-note"
      role="complementary"
      aria-label="Onboarding tour"
      style={noteStyle}
    >
      {/* Tape strip at top center */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -10,
          left: '50%',
          transform: 'translateX(-50%) rotate(2deg)',
          width: 48,
          height: 16,
          background: STICKY_TAPE,
          borderRadius: 1,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        }}
      />

      {/* Header: title only. The "Onboarding · 5 steps" eyebrow was
         removed 2026-05-26 per Matt — the step list and progress footer
         already communicate "5 steps" so the eyebrow was redundant. */}
      <div>
        <h3 style={headerTitle}>How Darwin sees the world</h3>
      </div>

      <ol
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          position: 'relative',
        }}
        aria-label="Onboarding steps"
      >
        {/* Dashed spine running through the step circles */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 10.5,
            top: 24,
            bottom: 24,
            width: 0,
            borderLeft: `1px dashed ${STICKY_BORDER}`,
            opacity: 0.7,
          }}
        />
        {STEPS.map((step) => {
          const isActive = step.index === activeIdx
          const isComplete = step.index < activeIdx
          return (
            <DesktopStepRow
              key={step.index}
              step={step}
              isActive={isActive}
              isComplete={isComplete}
              isLast={step.index === STEPS.length}
            />
          )
        })}
      </ol>

      {/* Footer: dashed divider + Skip link (only). "Don't show again"
         was removed per Matt 2026-05-26 — it added underline noise without
         giving the user a meaningfully different action at this surface.
         marginTop: auto pushes the footer to the bottom of the fixed-height
         note so steps 1–4 (with less content) don't leave the footer
         floating in the middle. */}
      <div
        style={{
          marginTop: 'auto',
          paddingTop: 10,
          borderTop: `1px dashed ${STICKY_BORDER}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div
          data-testid="coach-progress-footer"
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: STICKY_MUTED,
          }}
        >
          {activeIdx} of 5 complete
        </div>

        {showDismissControls && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: STICKY_MUTED,
            }}
          >
            <button
              type="button"
              data-testid="coach-dismiss-session"
              onClick={onSessionDismiss}
              style={stickyLinkStyle()}
            >
              Skip
            </button>
            {/* "Don't show again" is still wired up via persistent dismiss
               but lives in user settings now (Settings → Show onboarding tour).
               The coach itself only exposes the lightweight session-dismiss. */}
            <button
              type="button"
              data-testid="coach-dismiss-persistent"
              onClick={() => {
                try { window.localStorage.setItem(DISMISS_KEY, 'true') } catch { /* ignore */ }
                onPersistentDismiss()
              }}
              aria-label="Don't show again"
              style={{ ...stickyLinkStyle(), fontSize: 11, opacity: 0.65 }}
            >
              Don't show again
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

function stickyLinkStyle(): CSSProperties {
  return {
    background: 'transparent',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  }
}

// Faded "pending" gray for unreached steps. Per Matt 2026-05-26: "show
// the entire full text at once, faded in gray, then at each step make the
// tick blue and the text black. Nothing should move on the coach apart
// from the color changes as each step happens."
const PENDING_TITLE = '#9A9684'
const PENDING_DESC  = '#B5B19E'

function DesktopStepRow({
  step, isActive, isComplete,
}: {
  step: StepDef
  isActive: boolean
  isComplete: boolean
  isLast: boolean
}): ReactElement {
  const circleSize = 22

  // Binary state: reached (active OR complete) = blue+black; pending = gray.
  // We deliberately treat active and complete the same color-wise so the
  // only thing that changes per step is one row going from gray → dark,
  // never any layout movement.
  const reached = isComplete || isActive
  const circleBg = reached ? L.signal : 'transparent'
  const circleBorder = reached ? L.signal : PENDING_TITLE
  const circleColor = reached ? '#fff' : PENDING_TITLE
  const titleColor = reached ? STICKY_INK : PENDING_TITLE
  const descColor  = reached ? STICKY_DESC : PENDING_DESC
  const titleWeight = isActive ? 700 : 600

  const rowStyle: CSSProperties = {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: '6px 0',
    position: 'relative',
  }

  return (
    <li
      data-testid={isActive ? 'coach-step-active' : `coach-step-${step.index}`}
      data-step-index={step.index}
      aria-current={isActive ? 'step' : undefined}
      style={rowStyle}
    >
      <span
        aria-hidden="true"
        style={{
          width: circleSize,
          height: circleSize,
          borderRadius: '50%',
          background: circleBg,
          border: `1px solid ${circleBorder}`,
          color: circleColor,
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 11,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 1,
          zIndex: 1,
          transition: 'background 200ms ease, color 200ms ease, border-color 200ms ease',
        }}
      >
        {isComplete ? (
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M2 5.5l2.5 2.5L9 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : step.index}
      </span>

      <div style={{ minWidth: 0, flex: 1 }}>
        {/* Title + description render at all phases; only the color
           changes per step. No inline expansion (benefits removed from
           per-step rendering 2026-05-26 per Matt's "nothing should move"). */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: titleWeight,
              color: titleColor,
              lineHeight: 1.3,
              transition: 'color 200ms ease',
            }}
          >
            {step.title}
          </span>
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: descColor,
            marginTop: 2,
            lineHeight: 1.35,
            transition: 'color 200ms ease',
          }}
        >
          {step.description}
        </div>
      </div>
    </li>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Mobile sticky note (coach-mark variant)
// ─────────────────────────────────────────────────────────────────────

function MobileStickyNote({
  phase, showDismissControls, onSessionDismiss, onPersistentDismiss,
  streamingPastSubmitHighlight,
}: OnboardingCoachProps): ReactElement {
  const activeIdx = phaseToStepIndex(phase, streamingPastSubmitHighlight)
  const step = STEPS[activeIdx - 1] ?? STEPS[0]

  // Cycle the 3 benefit bullets when step 5 is active.
  const [benefitIdx, setBenefitIdx] = useState(0)
  const isStep5 = activeIdx === 5
  useCycleEffect(isStep5, 1200, BENEFITS.length, setBenefitIdx)
  const activeBenefit = isStep5 ? BENEFITS[benefitIdx % BENEFITS.length] : null

  // Mobile note: smaller than desktop, centered with off-center tape (left),
  // downward caret at bottom pointing at the input bar below it. Tilt is
  // gentler (-1.5°) because portrait viewport is narrower.
  const noteStyle: CSSProperties = {
    width: 'calc(78% - 8px)',
    margin: '6px auto 14px',
    padding: '11px 13px 9px',
    background: STICKY_BG,
    borderRadius: 4,
    fontFamily: 'Inter, system-ui, sans-serif',
    color: STICKY_INK,
    transform: 'rotate(-1.5deg)',
    transformOrigin: 'top center',
    boxShadow:
      '0 1px 0 rgba(0,0,0,0.04), ' +
      '0 5px 14px -7px rgba(70, 50, 0, 0.22), ' +
      '0 16px 26px -14px rgba(70, 50, 0, 0.10)',
    position: 'relative',
  }

  return (
    <div
      data-testid="onboarding-coach"
      data-variant="sticky-note"
      role="complementary"
      aria-label="Onboarding tour"
      style={noteStyle}
    >
      {/* Off-center tape strip (left, rotated) */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -8,
          left: '26%',
          transform: 'translateX(-50%) rotate(-6deg)',
          width: 42,
          height: 13,
          background: STICKY_TAPE,
          borderRadius: 1,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        }}
      />

      {/* Caret pointing at the input bar below */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -8,
          left: '50%',
          transform: 'translateX(-50%) rotate(-1.5deg)',
          width: 0,
          height: 0,
          borderLeft: '9px solid transparent',
          borderRight: '9px solid transparent',
          borderTop: `9px solid ${STICKY_BG}`,
          filter: 'drop-shadow(0 2px 1px rgba(70,50,0,0.08))',
        }}
      />

      <div
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 9.5,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: STICKY_MUTED,
          marginBottom: 2,
        }}
        data-testid="coach-mobile-counter"
      >
        Step {activeIdx} of 5
      </div>

      <div
        data-testid="coach-step-active"
        data-step-index={activeIdx}
        aria-current="step"
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: L.signal,
            color: '#fff',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 0 0 4px rgba(27, 79, 224, 0.15)',
          }}
        >
          {activeIdx}
        </span>
        <h3
          style={{
            fontFamily: CAVEAT_STACK,
            fontSize: 24,
            fontWeight: 600,
            margin: 0,
            lineHeight: 1.05,
            letterSpacing: '0.005em',
            color: STICKY_INK,
          }}
        >
          {step.title}
        </h3>
      </div>

      {/* Description — single line, trimmed. Step 5 cycles benefits in place. */}
      <div
        style={{
          fontSize: 12.5,
          color: STICKY_DESC,
          lineHeight: 1.4,
          marginBottom: 8,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        data-testid="coach-mobile-description"
      >
        {activeBenefit ? activeBenefit.textShort : step.descriptionShort}
      </div>

      {/* 5-segment progress strip */}
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 6,
        }}
      >
        {STEPS.map((s) => {
          const done = s.index < activeIdx
          const active = s.index === activeIdx
          return (
            <span
              key={s.index}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                background: done || active ? L.signal : 'rgba(183, 154, 64, 0.30)',
                boxShadow: active ? '0 0 0 2px rgba(27, 79, 224, 0.12)' : 'none',
                transition: 'background 250ms ease',
              }}
            />
          )
        })}
      </div>

      {/* Footer — Skip only (primary). "Don't show again" lives in
         settings per Matt 2026-05-26. */}
      {showDismissControls && (
        <div
          style={{
            paddingTop: 6,
            borderTop: `1px dashed ${STICKY_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <button
            type="button"
            data-testid="coach-dismiss-session"
            onClick={onSessionDismiss}
            style={{
              ...stickyLinkStyle(),
              fontFamily: 'Inter, sans-serif',
              fontSize: 11.5,
              fontWeight: 500,
              color: STICKY_INK,
              textDecoration: 'none',
            }}
          >
            Skip
          </button>
          <button
            type="button"
            data-testid="coach-dismiss-persistent"
            onClick={() => {
              try { window.localStorage.setItem(DISMISS_KEY, 'true') } catch { /* ignore */ }
              onPersistentDismiss()
            }}
            aria-label="Don't show again"
            style={{
              ...stickyLinkStyle(),
              fontFamily: 'Inter, sans-serif',
              fontSize: 11,
              fontWeight: 400,
              color: STICKY_MUTED,
            }}
          >
            Don't show again
          </button>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// DemoWatchNote — "This is a demo" sticky indicator (Part B, 2026-05-27).
//
// Extends the existing OnboardingCoach sticky-note visual language (same
// STICKY_BG, tape strip, shadow, Caveat font, rotation) — NOT a new component
// built from scratch. Matt picked option #3 (sticky-note coach overlay).
//
// Sizing — Matt's explicit ask: "be cautious with sizing."
//   Mobile:  max-width min(220px, 35vw), font 12-14px
//   Desktop: max-width min(280px, 25vw), font 12-14px
//
// Position: absolute, anchored to top-right corner of the demo viewport
// container (which has position:relative). On mobile and desktop both use
// top-right; the demo viewport is the result-pill area, not the page.
//
// The note is pointer-events:none so it never steals clicks from the demo.
//
// Countdown: driven by elapsedMs + totalDemoMs passed from EmptyStateDemo.
// No independent timer is introduced here — both values come from the
// EmptyStateDemo step-machine clock.
// ─────────────────────────────────────────────────────────────────────────────

export interface DemoWatchNoteProps {
  /** Whether we're in mobile layout. Drives size + font constraints. */
  isMobile: boolean
  /** True once the demo has played through one complete loop. */
  demoCompleted: boolean
  /** Milliseconds elapsed since the demo typing phase began. */
  elapsedMs: number
  /** Total content duration in ms, derived from step-machine constants. */
  totalDemoMs: number
}

/** Format ms as M:SS */
function fmtTime(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * DemoWatchNote — sticky note anchored to the demo viewport corner.
 * Shows "I'm Darwin. Watch me work" during playback and "Your turn" on
 * completion. Reuses the OnboardingCoach visual language (same file).
 */
export function DemoWatchNote({
  isMobile,
  demoCompleted,
  elapsedMs,
  totalDemoMs,
}: DemoWatchNoteProps): ReactElement {
  // Sizing constraints per Matt (2026-05-27): "be cautious with sizing."
  // Mobile: max-width min(220px, 35vw); Desktop: max-width min(280px, 25vw).
  // Both keep font at 12-14px so it's readable but unobtrusive.
  const maxWidth = isMobile ? 'min(220px, 35vw)' : 'min(280px, 25vw)'
  const baseFontSize = isMobile ? 12 : 13

  const noteStyle: CSSProperties = {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 5,
    maxWidth,
    // Width auto but let max-width clamp it on narrow viewports.
    // Using inline max-width lets JSDOM tests read the style directly
    // to verify sizing (no getBoundingClientRect needed in jsdom).
    background: STICKY_BG,
    borderRadius: 4,
    padding: isMobile ? '10px 12px 9px' : '12px 14px 10px',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: baseFontSize,
    color: STICKY_INK,
    // Slight opposing rotation from the OnboardingCoach (which is -1.4/-1.5°)
    // so the two notes have different handwriting feels.
    transform: 'rotate(1.2deg)',
    transformOrigin: 'top right',
    boxShadow:
      '0 1px 0 rgba(0,0,0,0.04), ' +
      '0 6px 18px -8px rgba(70, 50, 0, 0.20), ' +
      '0 22px 32px -16px rgba(70, 50, 0, 0.10)',
    // Non-interactive: never steals clicks or hover state from demo content.
    pointerEvents: 'none',
    userSelect: 'none',
  }

  const headStyle: CSSProperties = {
    fontFamily: CAVEAT_STACK,
    fontSize: isMobile ? 16 : 18,
    fontWeight: 600,
    lineHeight: 1.15,
    color: STICKY_INK,
    margin: 0,
  }

  const bodyStyle: CSSProperties = {
    fontSize: 12, // 12px typography floor on mobile (PRI-535)
    color: STICKY_DESC,
    lineHeight: 1.4,
    marginTop: 3,
  }

  return (
    <aside
      data-testid="demo-watch-note"
      aria-label={demoCompleted ? 'Demo complete — your turn' : 'Demo in progress'}
      style={noteStyle}
    >
      {/* Tape strip — same visual as OnboardingCoach, offset to right-of-center */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -8,
          right: '30%',
          transform: 'translateX(50%) rotate(-4deg)',
          width: 36,
          height: 12,
          background: STICKY_TAPE,
          borderRadius: 1,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        }}
      />

      {demoCompleted ? (
        // ── Completion state ─────────────────────────────────────────────
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <h4 style={headStyle}>Your turn —</h4>
          <p style={bodyStyle}>what do you want to ask me?</p>
          {/* Downward arrow pointing toward the prompt input below */}
          <div
            aria-hidden="true"
            data-testid="demo-watch-note-arrow"
            style={{
              textAlign: 'center',
              fontSize: 20,
              color: L.signal,
              marginTop: 4,
              lineHeight: 1,
            }}
          >
            ↓
          </div>
        </div>
      ) : (
        // ── In-progress state ────────────────────────────────────────────
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <h4 style={headStyle}>I'm Darwin.</h4>
          <p style={bodyStyle}>
            Watch me work for ~{Math.round(totalDemoMs / 1000)}s, then you'll get to try.
          </p>
          {/* Countdown — derived from step-machine clock, not a new timer */}
          <div
            data-testid="demo-watch-countdown"
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 12, // 12px typography floor on mobile (PRI-535)
              color: STICKY_MUTED,
              marginTop: 3,
              letterSpacing: '0.08em',
            }}
          >
            {fmtTime(Math.min(elapsedMs, totalDemoMs))} / {fmtTime(totalDemoMs)}
          </div>
        </div>
      )}
    </aside>
  )
}

// Tiny effect helper to cycle the step-5 benefits on mobile.
function useCycleEffect(
  enabled: boolean,
  intervalMs: number,
  modulo: number,
  setIdx: (updater: (prev: number) => number) => void,
): void {
  useEffect(() => {
    if (!enabled) {
      setIdx(() => 0)
      return
    }
    const t = window.setInterval(() => {
      setIdx((p) => (p + 1) % modulo)
    }, intervalMs)
    return () => window.clearInterval(t)
  }, [enabled, intervalMs, modulo, setIdx])
}

export default OnboardingCoach
